import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getBrandName } from "@finitless/shared/lib";
import QRCode from "qrcode";
import LocaleSelector from "@/components/ui/LocaleSelector";
import useUiLocale from "@/hooks/useUiLocale";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/finitless-store-app/id6756741074";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.finitless.storeapp";

const AndroidIcon = () => (
  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20">
    <svg
      className="h-7 w-7 text-emerald-400"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12" y2="18" />
    </svg>
  </div>
);

const AppleIcon = () => (
  <div
    className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
    style={{ backgroundColor: "#1f2937" }}
  >
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="#ffffff">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  </div>
);

const QrCard = ({ qrDataUrl, isLoading, title, description, icon }) => (
  <div className="flex-1 rounded-xl bg-gray-800/60 p-6">
    {icon}
    <h2
      className="mb-1 text-center text-xl font-bold"
      style={{ color: "#ffffff" }}
    >
      {title}
    </h2>
    <p
      className="mb-5 text-center text-sm"
      style={{ color: "rgba(255,255,255,0.7)" }}
    >
      {description}
    </p>
    <div
      className="flex items-center justify-center rounded-xl p-3"
      style={{ backgroundColor: "#ffffff" }}
    >
      {qrDataUrl ? (
        <img
          src={qrDataUrl}
          alt={title}
          className="w-full max-w-[240px] sm:max-w-[280px]"
        />
      ) : (
        <div className="flex aspect-square w-full max-w-[240px] items-center justify-center sm:max-w-[280px]">
          {isLoading ? (
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
          ) : (
            <span className="text-sm" style={{ color: "#9ca3af" }}>
              {description}
            </span>
          )}
        </div>
      )}
    </div>
  </div>
);

const DownloadAppPage = () => {
  const { t } = useTranslation("downloadApp");
  const navigate = useNavigate();
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const [iosQr, setIosQr] = useState("");
  const [androidQr, setAndroidQr] = useState("");
  const [isGeneratingQr, setIsGeneratingQr] = useState(true);

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  useEffect(() => {
    let cancelled = false;

    const generate = async () => {
      setIsGeneratingQr(true);
      try {
        const [iosDataUrl, androidDataUrl] = await Promise.all([
          QRCode.toDataURL(APP_STORE_URL, { width: 400, margin: 1 }),
          QRCode.toDataURL(PLAY_STORE_URL, { width: 400, margin: 1 }),
        ]);
        if (!cancelled) {
          setIosQr(iosDataUrl);
          setAndroidQr(androidDataUrl);
        }
      } catch {
        if (!cancelled) {
          setIosQr("");
          setAndroidQr("");
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingQr(false);
        }
      }
    };

    generate();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="dark flex min-h-screen flex-col bg-gray-950 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-3xl flex-1">
        {/* Top Bar */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate("/orders")}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            {t("back")}
          </button>
          <LocaleSelector
            id="download-locale-selector"
            label={t("language.label", { ns: "app" })}
            showLabel={false}
            showLeadingIcon={false}
            value={locale}
            onChange={handleLocaleChange}
            options={supportedLocales}
            selectClassName="h-9 min-w-[124px] border-white/15 bg-slate-950/70 text-[13px] shadow-none"
          />
        </div>

        {/* Header */}
        <div className="mb-10 text-center">
          <h1
            className="text-3xl font-bold sm:text-4xl"
            style={{ color: "#ffffff" }}
          >
            {t("title")}
          </h1>
          <p
            className="mt-3 text-base"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            {t("subtitle")}
          </p>
        </div>

        {/* Cards */}
        <div className="flex flex-col gap-6 sm:flex-row">
          {/* Android Card */}
          <QrCard
            icon={<AndroidIcon />}
            title={t("android.title")}
            description={t("android.description")}
            qrDataUrl={androidQr}
            isLoading={isGeneratingQr}
          />

          {/* iOS Card */}
          <QrCard
            icon={<AppleIcon />}
            title={t("ios.title")}
            description={t("ios.description")}
            qrDataUrl={iosQr}
            isLoading={isGeneratingQr}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-white/60">
          {t("footer", {
            year: new Date().getFullYear(),
            brand: getBrandName("Finitless"),
          })}
        </p>
      </div>
    </div>
  );
};

export default DownloadAppPage;
